import { useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { ProductTypeName } from '@electricjs/core_entity-client';
import {
  Text,
  Button,
  Flex,
  spaces,
  TextInputField,
  Banner,
  Box,
  InternationalPhoneInputField,
  type Country,
} from '@electricjs/arc';

import { ExternalLink } from '@common';

import { CURRENT_SHOPPING_COUNTRY_CODES } from '@/constants/storefront';
import { getLocalizedSupportUrl } from '@/helpers/getLocalizedSupportUrl';
import { getCountryName } from '@/helpers/getCountryName';
import { useGetOrganizationId } from '@/hooks/useGetOrganizationId';
import { useGlobalUI } from '@/components/GlobalUIProvider';
import {
  AddressForm,
  AddressValidationModal,
  useAddressValidation,
} from '@/components/AddressValidation';
import { useLazyGetPaginatedProductsQuery } from '@/redux/slices/productApiSlice';
import { useStorefront } from '@/components/Storefront/StorefrontProvider';
import { FirstStepFormData } from '../OtherCountryFlowProvider';

export const FirstStepTile = () => {
  const { t } = useTranslation('storefront');

  const { openModal, closeModal, showErrorToast } = useGlobalUI();

  const {
    otherCountryFlowFirstStepForm,
    setOtherCountryFlowStep,
    storefrontCheckoutForm,
  } = useStorefront();

  const { validateAddress, isValidating: isValidatingAddress } =
    useAddressValidation();

  const organizationId = useGetOrganizationId();

  if (!otherCountryFlowFirstStepForm) {
    throw new Error();
  }

  const {
    handleSubmit,
    control,
    setValue,
    getValues,
    trigger,
    watch,
    setError,
  } = otherCountryFlowFirstStepForm;

  const [
    getPaginatedProducts,
    {
      data: paginatedProducts,
      error: productsError,
      isFetching: isFetchingProducts,
    },
  ] = useLazyGetPaginatedProductsQuery();

  const products = paginatedProducts?.paginatedResponse.results;

  const isCountrySupported = products && products.length > 0;

  const selectedAddressCountryCode = watch('address.country') as Country;

  // Watch for country changes and trigger international products query to check if the country is supported
  useEffect(() => {
    if (selectedAddressCountryCode && organizationId) {
      getPaginatedProducts(
        {
          offset: 0,
          limit: 1, // only one product is enough to check if the country is supported
          productTypes: [ProductTypeName.Laptop],
          countryCode: selectedAddressCountryCode,
        },
        true
      )
        .unwrap()
        .catch(() => {
          showErrorToast({
            id: 'country-validation-error-toast',
            message: t(
              'international.firstStep.errors.countryValidationFailed'
            ),
          });
        });
    }
  }, [
    selectedAddressCountryCode,
    organizationId,
    getPaginatedProducts,
    showErrorToast,
    t,
  ]);

  const onSubmit = (formData: FirstStepFormData) => {
    // Manually validate the employee field (as it belongs to other form)
    if (!storefrontCheckoutForm?.getValues('employee.value')) {
      storefrontCheckoutForm?.trigger('employee', { shouldFocus: true });
      return;
    }

    // Manually validate the country part of the address (as it requires special handling)
    if (CURRENT_SHOPPING_COUNTRY_CODES.includes(selectedAddressCountryCode)) {
      setError('address.country', {
        message: t('international.firstStep.errors.domesticCountryNotAllowed', {
          countryName: getCountryName(selectedAddressCountryCode),
        }),
      });
      return;
    } else if (!isCountrySupported) {
      setError('address.country', {
        message: t('international.firstStep.errors.countryNotSupported'),
      });
      return;
    }

    // Validate address exactness using Google's API and show modal if needed
    validateAddress(
      formData.address,
      // success callback
      validationResult => {
        if (validationResult?.isExactMatch) {
          setOtherCountryFlowStep(2); // go to second step
          return;
        }
        openModal(
          <AddressValidationModal
            isVisible
            onClose={closeModal}
            onConfirm={async selectedAddress => {
              const originalCountry = formData.address.country;

              // update the form with the new selected address (selected in the modal)
              setValue('address', selectedAddress, {
                shouldValidate: true,
                shouldDirty: true,
              });

              // in case the country was changed by the modal
              if (selectedAddress.country !== originalCountry) {
                // check if the new country has its own store
                if (
                  CURRENT_SHOPPING_COUNTRY_CODES.includes(
                    selectedAddress.country as Country
                  )
                ) {
                  // Has its own store. Close the modal to let the user see the error in the form.
                  closeModal();
                  return;
                }

                // check if the new country is supported (has products)
                const { data } = await getPaginatedProducts(
                  {
                    offset: 0,
                    limit: 1, // only one product is enough to check if the country is supported
                    productTypes: [ProductTypeName.Laptop],
                    countryCode: selectedAddress.country as Country,
                  },
                  true
                );
                if (!data || data.paginatedResponse.results.length === 0) {
                  // Not supported. Just close the modal to let the user see the error in the form.
                  closeModal();
                  return;
                }
              }

              setOtherCountryFlowStep(2); // go to second step
              closeModal();
            }}
            originalAddress={formData.address}
            validationResult={validationResult}
          />
        );
      },
      // error callback
      error => {
        showErrorToast({
          id: 'address-validation-error-toast',
          message: t('international.firstStep.errors.addressValidationFailed'),
        });

        console.error(error);
      }
    );
  };

  return (
    <Flex vertical width="100%">
      <Text mb={spaces.space_m_rem}>
        {t('international.firstStep.description')}
      </Text>
      <TextInputField
        id="recipient-id-input"
        name="recipientId"
        control={control}
        label={t('international.firstStep.labels.recipientId')}
        required
        helperText={
          <Text variant="legal" intent="secondary">
            <Trans
              i18nKey="international.firstStep.helperText.recipientIdFullText"
              ns="storefront"
              components={{
                ExternalLink: (
                  <ExternalLink
                    href={getLocalizedSupportUrl(
                      'https://support.electric.ai/electricithub/purchasing-for-employees-outside-of-the-u-s#PurchasingforemployeesoutsideoftheU.S.-ProvidingaNationalIDforhardwarepurchases'
                    )}
                    showIcon={false}
                    variant="legal">
                    {' '}
                  </ExternalLink>
                ),
              }}
            />
          </Text>
        }
      />
      <AddressForm
        name="address"
        key={JSON.stringify(getValues('address'))}
        control={control}
        trigger={trigger}
        onChange={(field, value) => {
          setValue(field, value, {
            shouldValidate: true,
            shouldDirty: true,
          });
        }}
        required
        showAddressSearch
      />

      {/* "Country not supported" banner 
            - Displayed when the country has 0 products available
            - Hidden before initial fetch (when products === undefined) and during the fetch        
      */}
      {selectedAddressCountryCode &&
        !isCountrySupported &&
        products &&
        !isFetchingProducts &&
        !productsError && (
          <Box width="100%" mb={spaces.space_l}>
            <Banner
              id="no-products-found-for-this-country-banner"
              isFlat
              isFullWidth
              intent="error"
              title={t('international.firstStep.banner.unsupportedCountry')}
              message={() => (
                <Text>
                  <Trans
                    i18nKey="international.firstStep.banner.unsupportedCountryMessageFullText"
                    ns="storefront"
                    components={{
                      ExternalLink: (
                        <ExternalLink
                          href="mailto:support@electric.ai"
                          showIcon={false}>
                          {' '}
                        </ExternalLink>
                      ),
                    }}
                  />
                </Text>
              )}
            />
          </Box>
        )}
      <InternationalPhoneInputField
        name="phone"
        control={control}
        label={t('international.firstStep.labels.phoneNumber')}
        required
      />
      <TextInputField
        id="email-input"
        name="email"
        control={control}
        label={t('international.firstStep.labels.email')}
        required
      />
      <Flex width="100%" justifyContent="flex-end">
        <Button
          id="continue-button"
          type="button"
          iconAfter="arrow-right"
          disabled={
            // Avoiding clicks when we can't determine whether the country is supported or not
            !products || isFetchingProducts || (productsError as boolean)
          }
          loading={isValidatingAddress}
          onClick={handleSubmit(onSubmit)}>
          {t('international.firstStep.buttons.continue')}
        </Button>
      </Flex>
    </Flex>
  );
};
